import { AddressAutocomplete, FormInput, SearchableSelect } from '@/shared/components'
import { licenseStates, normalizeStateName } from '../../../components/eligibilitySteps/states'
import { ENROLLMENT_FIELD_LABELS } from '../../../constants/enrollmentContent'
import type { EnrollmentFieldEditability } from '../../../utils/enrollmentHydration'
import type {
  AddressParts,
  ApplicationFormData,
  FieldErrorKey,
  FieldErrors,
  UpdateApplicationField
} from '../types'

type ApplicationAddressFieldsProps = {
  addressStateRestriction: string
  fieldErrors: FieldErrors
  fieldEditability: EnrollmentFieldEditability
  formData: ApplicationFormData
  fullAddress: string
  onAddressChange: (value: string) => void
  onAddressSelect: (parts: AddressParts) => void
  onClearFieldError: (field: FieldErrorKey) => void
  onFieldChange: UpdateApplicationField
}

type RequiredLabelProps = {
  text: string
}

function RequiredLabel({ text }: RequiredLabelProps) {
  return (
    <span>
      {text} <span className="text-red-500">*</span>
    </span>
  )
}

type FieldErrorMessageProps = {
  message?: string
}

function FieldErrorMessage({ message }: FieldErrorMessageProps) {
  if (!message) {
    return null
  }

  return <p className="mt-1 text-xs text-red-500">{message}</p>
}

const toZipCode = (value: string) => value.replace(/\D/g, '').slice(0, 5)

export function ApplicationAddressFields({
  addressStateRestriction,
  fieldErrors,
  fieldEditability,
  formData,
  fullAddress,
  onAddressChange,
  onAddressSelect,
  onClearFieldError,
  onFieldChange
}: ApplicationAddressFieldsProps) {
  const isFullAddressDisabled =
    fieldEditability.addressLine1 === false ||
    fieldEditability.city === false ||
    fieldEditability.state === false ||
    fieldEditability.zipCode === false

  return (
    <>
      <div className="md:col-span-2">
        <AddressAutocomplete
          label={<RequiredLabel text={ENROLLMENT_FIELD_LABELS.fullAddress} />}
          stateRestriction={addressStateRestriction}
          placeholder={ENROLLMENT_FIELD_LABELS.fullAddress}
          delay={480}
          value={fullAddress}
          disabled={isFullAddressDisabled}
          error={!!fieldErrors.addressLine1}
          onChange={onAddressChange}
          onAddressSelect={onAddressSelect}
        />
        <FieldErrorMessage message={fieldErrors.addressLine1} />
      </div>
      <div className="grid grid-cols-1 gap-4 md:col-span-2 md:grid-cols-3">
        <div>
          <FormInput
            label={<RequiredLabel text={ENROLLMENT_FIELD_LABELS.city} />}
            placeholder={ENROLLMENT_FIELD_LABELS.city}
            delay={520}
            value={formData.city}
            disabled={fieldEditability.city === false}
            error={!!fieldErrors.city}
            onChange={value => {
              onFieldChange('city', value)
              onClearFieldError('city')
            }}
          />
          <FieldErrorMessage message={fieldErrors.city} />
        </div>
        <div>
          <SearchableSelect
            label={ENROLLMENT_FIELD_LABELS.state}
            options={licenseStates}
            value={normalizeStateName(formData.state)}
            onChange={value => {
              onFieldChange('state', value)
              onClearFieldError('state')
            }}
            placeholder="Select state"
            disabled={fieldEditability.state === false}
            error={fieldErrors.state}
            required
          />
        </div>
        <div>
          <FormInput
            label={<RequiredLabel text={ENROLLMENT_FIELD_LABELS.zipCode} />}
            placeholder={ENROLLMENT_FIELD_LABELS.zipCode}
            delay={560}
            value={formData.zipCode}
            disabled={fieldEditability.zipCode === false}
            error={!!fieldErrors.zipCode}
            onChange={value => {
              onFieldChange('zipCode', toZipCode(value))
              onClearFieldError('zipCode')
            }}
          />
          <FieldErrorMessage message={fieldErrors.zipCode} />
        </div>
      </div>
    </>
  )
}
